import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as Yup from 'yup';
import merge from 'lodash/merge';
import FormProvider, {
  RHFSelect,
  RHFTextField,
  RHFCheckbox,
  RHFSwitch,
  RHFUploadAvatar,
} from '../../../components/hook-form';
import { UploadService } from '../../../api/upload.service';
import { useCallback } from 'react';
import {
  Box,
  Stack,
  Button,
  Tooltip,
  DialogActions,
  Typography,
  Divider,
  Avatar,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers';
import { yupResolver } from '@hookform/resolvers/yup';
import { IPatientSchema } from '../../../@types/patient';
import { enqueueSnackbar } from 'notistack';
import {
  patientGeocodingLocationUrl,
  postalCode,
} from '../../../config-global';
import { PatientService } from '../../../api/patient.service';
import { useAuthContext } from '../../../auth/useAuthContext';
import { usePatientPermissions } from '../../../hooks/usePatientPermissions';
import NHSEyeTestEligibility, {
  NHSEligibilityData,
} from '../../../components/patient/NHSEyeTestEligibility';
import { useAppContext } from '../../../providers/AppContext';

import ConfirmDialog from '../../../components/confirm-dialog';
import { getR2File } from '../../../utils/file';
import { programmaticSetValueOptions } from '../../../utils/form-unsaved-close.util';
import { toUpperText } from '../../../utils/uppercase';
import {
  isValidPhoneNumber,
  PHONE_VALIDATION_MESSAGE,
  stripNonPhoneCharacters,
} from '../../../utils/phone';
import {
  capitalizeWords,
  joinAddressParts,
  splitInitialName,
} from './patientFormUtils';

/** Mirrors the patient column sizes in optopro-backend create-patient.dto.ts. */
const PATIENT_NAME_MAX = 100;
const PATIENT_SALUTATION_MAX = 120;
/** UK postcode, e.g. "SW1A 1AA", "M1 1AE", "B33 8TH". */
const UK_POSTCODE_PATTERN = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;

type Props = {
  onCancel: VoidFunction;
  isEdit: Boolean;
  currentPatient?: IPatientSchema;
  Fields: Boolean;
  onSubmitForm: (patient?: any) => void;
  readOnly?: boolean;
  onDirtyStateChange?: (isDirty: boolean) => void;
  /**
   * Close handler used after a successful save. Unlike `onCancel` it must not
   * run the unsaved-changes guard — the data is already persisted, so there is
   * nothing to discard. Defaults to `onCancel` for callers without a guard.
   */
  onCloseAfterSave?: VoidFunction;
  /**
   * Name typed elsewhere (e.g. the appointment patient search). On a new
   * patient it prefills First Name (first word) and Surname (the rest).
   */
  initialName?: string;
};

type FormValuesProps = IPatientSchema & { gpAddress?: string };
const today = new Date();

const getAge = (date: Date) => {
  return (
    today.getFullYear() -
    date.getFullYear() -
    (today.getMonth() < date.getMonth() ||
    (today.getMonth() === date.getMonth() && today.getDate() < date.getDate())
      ? 1
      : 0)
  );
};

const getInitialValues = (
  currentPatient: IPatientSchema | null | undefined,
  initialName?: string,
) => {
  const initialEvent: FormValuesProps = {
    id: '',
    title: '',
    firstName: '',
    surName: '',
    lastName: '',
    gp: '',
    gpAddress: '',
    date_of_birth: null,
    postCode: '',
    gender: 'Male',
    phone: '',
    mobile: '',
    landline: '',
    email: '',
    town: '',
    county: '',
    houseNumber: '',
    street: '',
    address: '',
    age: null,
    notes: '',
    popupNotes: false,
    nhsEyeTestEligibility: {
      under16: false,
      over60: false,
      student: false,
      benefits: false,
      glaucomaDiabetes: false,
      familyHistory: false,
      other: false,
      otherDetails: '',
      evidenceSeen: false,
      auditLog: [],
    },
    communicationStyle: {
      method: undefined,
      customText: '',
    },
    consentSMS: false,
    consentEmail: false,
    consentPhone: false,
    consentPost: false,
    isActive: true,
  };

  if (currentPatient) {
    if (currentPatient.date_of_birth) {
      currentPatient.age = getAge(new Date(currentPatient.date_of_birth));
    }

    const gpDetailsName = (currentPatient as any)?.gpDetails?.name;
    const gpDetailsAddress = (currentPatient as any)?.gpDetails?.address;

    const gpMapped = {
      // GP = a practitioner name, held in CAPITALS.
      gp: toUpperText(gpDetailsName ?? currentPatient.gp ?? ''),
      gpAddress: gpDetailsAddress ?? (currentPatient as any).gpAddress ?? '',
    } as Partial<FormValuesProps>;

    const pref = (currentPatient as any)?.patientPreference;
    const mappedConsents = {
      consentSMS:
        pref?.smsConsent ??
        (currentPatient as any).gdprSmsConsent ??
        currentPatient.consentSMS ??
        false,
      consentEmail:
        pref?.emailConsent ??
        (currentPatient as any).gdprEmailConsent ??
        currentPatient.consentEmail ??
        false,
      consentPhone:
        pref?.phoneConsent ??
        (currentPatient as any).gdprPhoneConsent ??
        currentPatient.consentPhone ??
        false,
      consentPost:
        pref?.postConsent ??
        (currentPatient as any).gdprPostConsent ??
        currentPatient.consentPost ??
        false,
    } as Partial<FormValuesProps>;

    const nhs = (currentPatient as any)?.patientNhs?.nhsEligibility;
    const nhsEyeTestEligibility = nhs
      ? {
          under16: !!nhs.isUnder16,
          over60: !!nhs.isOver60,
          student: !!nhs.isStudent,
          benefits: !!nhs.isBenefits,
          glaucomaDiabetes: !!nhs.isGlaucomaDiabetes,
          familyHistory: !!nhs.isFamilyHistory,
          other: !!nhs.isOther,
          otherDetails: (nhs.isOther as string) || '',
          evidenceSeen: !!nhs.isEvidenceSeen,
          auditLog: [],
        }
      : initialEvent.nhsEyeTestEligibility;

    const mappedCommunicationStyle =
      typeof (currentPatient as any).communicationStyle === 'string'
        ? {
            method: (currentPatient as any).communicationStyle,
            customText: '',
          }
        : (currentPatient as any).communicationStyle ||
          initialEvent.communicationStyle;

    const mappedContact = {
      mobile:
        (currentPatient as any)?.mobile || (currentPatient as any)?.phone || '',
      phone:
        (currentPatient as any)?.phone ?? (currentPatient as any)?.mobile ?? '',
      landline: (currentPatient as any)?.landline ?? '',
      postCode:
        (currentPatient as any)?.postCode ??
        (currentPatient as any)?.postalCode ??
        '',
      houseNumber:
        (currentPatient as any)?.houseNumber ??
        (currentPatient as any)?.address?.houseNumber ??
        '',
      street:
        (currentPatient as any)?.street ??
        (currentPatient as any)?.address?.street ??
        '',
      town:
        (currentPatient as any)?.town ??
        (currentPatient as any)?.address?.city ??
        '',
      county:
        (currentPatient as any)?.county ??
        (currentPatient as any)?.address?.province ??
        '',
      address:
        ((currentPatient as any)?.address ??
          [
            (currentPatient as any)?.houseNumber,
            (currentPatient as any)?.street,
            (currentPatient as any)?.town,
            (currentPatient as any)?.county,
            (currentPatient as any)?.postCode ??
              (currentPatient as any)?.postalCode,
          ]
            .filter(Boolean)
            .join(', ')) ||
        '',
    } as Partial<FormValuesProps>;

    return merge(
      {},
      initialEvent,
      currentPatient,
      gpMapped,
      mappedConsents,
      { communicationStyle: mappedCommunicationStyle },
      { nhsEyeTestEligibility },
      mappedContact,
    );
  }

  if (initialName) {
    return { ...initialEvent, ...splitInitialName(initialName) };
  }

  return initialEvent;
};
const titles = [
  { id: 'Mr', name: 'Mr' },
  { id: 'Mrs', name: 'Mrs' },
  { id: 'Miss', name: 'Miss' },
  { id: 'Ms', name: 'Ms' },
  { id: 'Mx', name: 'Mx' },
  { id: 'Dr', name: 'Dr' },
  { id: 'Prof', name: 'Prof' },
];
const AddPatient = ({
  onCancel,
  isEdit,
  currentPatient,
  Fields,
  onSubmitForm,
  readOnly = false,
  onDirtyStateChange,
  onCloseAfterSave = onCancel,
  initialName,
}: Props) => {
  const { user } = useAuthContext();
  const perms = usePatientPermissions(user);
  const [showFields, setShowFields] = useState(false);
  const [showAddressFields, setShowAddressFields] = useState(Fields);
  const [checkPostCodeString, setCheckPostCodeString] = useState('');
  /**
   * True once the user has typed in the Postcode field. A stored postcode that
   * predates the UK-format rule must not block saving an existing patient the
   * user never touched (client item 3).
   */
  const postCodeEditedRef = React.useRef(false);
  /**
   * Last salutation this form generated from Title + Surname. Anything else in
   * the field was typed by the user (or loaded from the record) and is never
   * overwritten (client item 11).
   */
  const lastGeneratedSalutationRef = React.useRef<string | null>(null);
  /** Name + DOB the user has already confirmed as "create anyway". */
  const confirmedDuplicateKeyRef = React.useRef<string | null>(null);
  const [town, setTown] = useState('');
  const [houseNumber, setHouseNumber] = useState('');
  const [county, setCounty] = useState('');
  const [street, setStreet] = useState('');

  const PatientSchema = Yup.object().shape({
    firstName: Yup.string()
      .required('First Name is required')
      .max(
        PATIENT_NAME_MAX,
        `First Name must be at most ${PATIENT_NAME_MAX} characters`,
      ),
    surName: Yup.string()
      .required('Surname is required')
      .max(
        PATIENT_NAME_MAX,
        `Surname must be at most ${PATIENT_NAME_MAX} characters`,
      ),
    lastName: Yup.string()
      .required('Salutation is required')
      .max(
        PATIENT_SALUTATION_MAX,
        `Salutation must be at most ${PATIENT_SALUTATION_MAX} characters`,
      ),
    // Optional: not every patient has an email address.
    email: Yup.string()
      .nullable()
      .trim()
      .email('Email must be a valid email address')
      .max(255, 'Email must be at most 255 characters'),
    gender: Yup.string().required('Gender is required'),
    date_of_birth: Yup.date()
      .required('Date of birth is required')
      // maxDate only greys out the calendar; a typed date still gets through.
      .max(new Date(), 'Date of birth cannot be in the future'),
    // Must be sendable by the SMS provider (E.164 after normalisation).
    // Optional: a patient may not have a mobile. If given it must be valid.
    mobile: Yup.string().test(
      'valid-phone',
      PHONE_VALIDATION_MESSAGE,
      (value) => !value || isValidPhoneNumber(value),
    ),
    landline: Yup.string().test(
      'valid-landline',
      PHONE_VALIDATION_MESSAGE,
      (value) => !value || isValidPhoneNumber(value),
    ),
    postCode: Yup.string()
      .required('Postcode is required')
      .test(
        'uk-postcode',
        'Enter a valid UK postcode, e.g. SW1A 1AA',
        (value) =>
          // An existing record whose stored postcode predates this rule stays
          // savable as long as the user hasn't retyped the field.
          (!!isEdit && !postCodeEditedRef.current) ||
          UK_POSTCODE_PATTERN.test(String(value ?? '').trim()),
      ),
    // Whitespace alone is not an address.
    address: Yup.string().trim().required('Full Address is required'),
    notes: Yup.string(),
    // A patient may not know their GP details when registering.
    gp: Yup.string(),
    gpAddress: Yup.string(),
  });

  const methods = useForm<any>({
    resolver: yupResolver(PatientSchema),
    defaultValues: getInitialValues(currentPatient, initialName),
  });
  const [postalCodeResponse, setPostalCodeResponse] = useState<any[]>([]);

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { isSubmitting, isDirty },
    getValues,
  } = methods;

  useEffect(() => {
    reset(getInitialValues(currentPatient, initialName));
    // A different record: nothing has been typed or confirmed in it yet.
    lastGeneratedSalutationRef.current = null;
    postCodeEditedRef.current = false;
    confirmedDuplicateKeyRef.current = null;
    // initialName only seeds a new record; retyping it elsewhere must not
    // wipe what the user has entered here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPatient?.id, isEdit, reset]);

  useEffect(() => {
    onDirtyStateChange?.(isDirty);
  }, [isDirty, onDirtyStateChange]);

  const closeAfterSave = useCallback(() => {
    onDirtyStateChange?.(false);
    onCloseAfterSave();
  }, [onDirtyStateChange, onCloseAfterSave]);

  const patientService = new PatientService();
  const uploadService = new UploadService();

  const handleDrop = useCallback(
    async (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) {
        setValue(
          'imageUrl',
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          }) as any,
          { shouldValidate: true },
        );
      }
    },
    [setValue],
  );

  const imageUrlValue = watch('imageUrl');

  const hasPatientImage = Boolean(
    imageUrlValue &&
    (typeof imageUrlValue === 'string'
      ? imageUrlValue.trim()
      : imageUrlValue instanceof File ||
        (imageUrlValue as File & { preview?: string })?.preview),
  );

  const handleRemoveImage = useCallback(() => {
    const current = getValues('imageUrl');
    if (
      current &&
      typeof current === 'object' &&
      'preview' in current &&
      typeof (current as { preview?: string }).preview === 'string'
    ) {
      URL.revokeObjectURL((current as { preview: string }).preview);
    }
    setValue('imageUrl', null, { shouldDirty: true, shouldValidate: true });
  }, [getValues, setValue]);

  const resolveFinalImageUrl = async (
    imageUrl: unknown,
  ): Promise<string | null | undefined> => {
    if (imageUrl === null || imageUrl === '') {
      return null;
    }
    if (typeof imageUrl === 'string' && imageUrl.trim()) {
      return imageUrl;
    }
    if (imageUrl && (imageUrl as File) instanceof File) {
      const response = await uploadService.uploadFile(
        imageUrl as File,
        'patients',
      );
      return response.data?.url ?? null;
    }
    return undefined;
  };

  const lastDuplicateKeyRef = React.useRef<string | null>(null);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  /** Save held back until the user confirms a possible duplicate patient. */
  const [duplicateConfirm, setDuplicateConfirm] = useState<{
    data: FormValuesProps;
    mode: 'create' | 'update';
  } | null>(null);
  const [isSavingDuplicate, setIsSavingDuplicate] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const normalizeName = (s: any) =>
    String(s ?? '')
      .trim()
      .toLowerCase();

  const normalizeDateParts = (input: any) => {
    if (!input) return null;
    try {
      let d: Date;
      if (input instanceof Date) {
        d = input;
      } else if (typeof input === 'string') {
        const s = input.trim();
        if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
          // Calendar date: build it locally so no timezone shifts the day.
          d = new Date(
            Number(s.slice(0, 4)),
            Number(s.slice(5, 7)) - 1,
            Number(s.slice(8, 10)),
          );
        } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
          const [day, month, year] = s.split('/').map(Number);
          d = new Date(year, month - 1, day);
        } else {
          d = new Date(s);
        }
      } else if (input?.$d) {
        d = new Date(input.$d);
      } else if (typeof input === 'object' && input?.toDate) {
        d = new Date(input.toDate());
      } else {
        d = new Date(input);
      }
      if (isNaN(d.getTime())) return null;
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return { y, m, day };
    } catch (e) {
      return null;
    }
  };

  type DuplicateQuery = {
    firstName: string;
    surName: string;
    dateOfBirthISO: string;
  };

  /** Name + DOB to check, or null while the three fields aren't all filled in. */
  const getDuplicateQuery = (
    values: Partial<FormValuesProps>,
  ): DuplicateQuery | null => {
    const { firstName, surName, date_of_birth } = values;
    if (!firstName || !surName || !date_of_birth) return null;
    const dobParts = normalizeDateParts(date_of_birth);
    if (!dobParts) return null;
    return {
      firstName,
      surName,
      dateOfBirthISO: `${dobParts.y}-${dobParts.m}-${dobParts.day}`,
    };
  };

  const duplicateKeyOf = (query: DuplicateQuery) =>
    `${normalizeName(query.firstName)}|${normalizeName(query.surName)}|${
      query.dateOfBirthISO
    }`;

  /**
   * True when another patient already has this name and date of birth.
   * When editing, a name/DOB that still matches the record can only find the
   * record itself, so that case is not a duplicate.
   */
  const isDuplicateNameDob = async (
    query: DuplicateQuery,
  ): Promise<boolean> => {
    if (isEdit) {
      const own = getDuplicateQuery({
        firstName: currentPatient?.firstName,
        surName: currentPatient?.surName,
        date_of_birth: currentPatient?.date_of_birth,
      } as Partial<FormValuesProps>);
      if (
        own &&
        normalizeName(own.firstName) === normalizeName(query.firstName) &&
        normalizeName(own.surName) === normalizeName(query.surName) &&
        own.dateOfBirthISO === query.dateOfBirthISO
      ) {
        return false;
      }
    }

    const response = await patientService.checkDuplicateByNameDob(
      query.firstName,
      query.surName,
      query.dateOfBirthISO,
      currentPatient?.id ? String(currentPatient.id) : undefined,
    );
    const { duplicates } = response.data || {};
    if (Array.isArray(duplicates) && duplicates.length > 0) {
      type DuplicateCandidate = {
        id?: unknown;
        firstName?: unknown;
        surName?: unknown;
        date_of_birth?: unknown;
      };
      return duplicates.some((candidate: DuplicateCandidate) => {
        if (
          currentPatient?.id &&
          String(candidate?.id) === String(currentPatient.id)
        ) {
          // The record being edited is not its own duplicate.
          return false;
        }
        const d = normalizeDateParts(candidate?.date_of_birth);
        return (
          normalizeName(candidate?.firstName) ===
            normalizeName(query.firstName) &&
          normalizeName(candidate?.surName) === normalizeName(query.surName) &&
          (d ? `${d.y}-${d.m}-${d.day}` : null) === query.dateOfBirthISO
        );
      });
    }
    return response.data?.isDuplicate === true;
  };

  // Warning used by the First Name / Surname / Date of Birth blur handlers.
  // Debounced by the last-checked key so tabbing through the fields only asks
  // the API once per name + DOB combination.
  const checkDuplicateByNameDob = () => {
    (async () => {
      try {
        const query = getDuplicateQuery(getValues());
        if (!query) return;
        const key = duplicateKeyOf(query);
        if (lastDuplicateKeyRef.current === key || isCheckingDuplicate) return;

        setIsCheckingDuplicate(true);
        lastDuplicateKeyRef.current = key;

        if (await isDuplicateNameDob(query)) {
          enqueueSnackbar(
            `Possible duplicate found: ${query.firstName} ${query.surName} with the same DOB already exists.`,
            { variant: 'warning', autoHideDuration: 4000 },
          );
        }
      } catch (error) {
        // Do not block user input
      } finally {
        setIsCheckingDuplicate(false);
      }
    })();
  };

  const handleDOBBlur = () => {
    checkDuplicateByNameDob();
  };

  // State for tracking consent changes
  const [consentChanges, setConsentChanges] = useState<any[]>([]);

  // State for NHS Eye Test Eligibility
  const [nhsEligibility, setNhsEligibility] = useState<NHSEligibilityData>(
    currentPatient?.nhsEyeTestEligibility || {
      under16: false,
      over60: false,
      student: false,
      benefits: false,
      glaucomaDiabetes: false,
      familyHistory: false,
      other: false,
      otherDetails: '',
      evidenceSeen: false,
      auditLog: [],
    },
  );

  const watchedConsentSMS = watch('consentSMS');
  const watchedConsentEmail = watch('consentEmail');
  const watchedConsentPhone = watch('consentPhone');
  const watchedConsentPost = watch('consentPost');

  const logConsentChange = (
    consentType: string,
    newValue: boolean,
    oldValue: boolean,
  ) => {
    if (newValue !== oldValue) {
      const changeLog = {
        consentType,
        newValue,
        oldValue,
        timestamp: new Date().toISOString(),
        staffUserID: user?.id || user?.userId || 'SYSTEM',
        changeReason: newValue ? 'Consent granted' : 'Consent withdrawn',
      };

      setConsentChanges((prev) => [...prev, changeLog]);
    }
  };

  useEffect(() => {
    const initialConsents = getInitialValues(currentPatient);
    if (watchedConsentSMS !== undefined) {
      logConsentChange(
        'SMS',
        watchedConsentSMS,
        initialConsents.consentSMS || false,
      );
    }
  }, [watchedConsentSMS]);

  useEffect(() => {
    const initialConsents = getInitialValues(currentPatient);
    if (watchedConsentEmail !== undefined) {
      logConsentChange(
        'Email',
        watchedConsentEmail,
        initialConsents.consentEmail || false,
      );
    }
  }, [watchedConsentEmail]);

  useEffect(() => {
    const initialConsents = getInitialValues(currentPatient);
    if (watchedConsentPhone !== undefined) {
      logConsentChange(
        'Phone',
        watchedConsentPhone,
        initialConsents.consentPhone || false,
      );
    }
  }, [watchedConsentPhone]);

  useEffect(() => {
    const initialConsents = getInitialValues(currentPatient);
    if (watchedConsentPost !== undefined) {
      logConsentChange(
        'Post',
        watchedConsentPost,
        initialConsents.consentPost || false,
      );
    }
  }, [watchedConsentPost]);

  const watchedTitle = watch('title');
  const watchedSurname = watch('surName');

  useEffect(() => {
    if (!watchedTitle || !watchedSurname) return;
    const generatedSalutation = `${watchedTitle} ${watchedSurname}`;
    const currentSalutation = String(getValues('lastName') ?? '').trim();
    if (
      lastGeneratedSalutationRef.current === null &&
      currentSalutation === generatedSalutation
    ) {
      // A loaded record that already reads "Title Surname": treat it as
      // generated so later title/surname edits keep it in step.
      lastGeneratedSalutationRef.current = generatedSalutation;
      return;
    }
    if (
      currentSalutation &&
      currentSalutation !== lastGeneratedSalutationRef.current
    ) {
      // Typed by the user or loaded from the record — leave it alone.
      return;
    }
    lastGeneratedSalutationRef.current = generatedSalutation;
    setValue('lastName', generatedSalutation, programmaticSetValueOptions);
  }, [watchedTitle, watchedSurname, setValue, getValues]);

  useEffect(() => {
    const currentTitle = watch('title');
    const currentSurname = watch('surName');
    const currentLastName = watch('lastName');

    if (currentTitle && currentSurname && !currentLastName) {
      const salutation = `${currentTitle} ${currentSurname}`;
      setValue('lastName', salutation, programmaticSetValueOptions);
    }
  }, []);

  useEffect(() => {
    const currentDOB = watch('date_of_birth');
    if (isEdit && currentDOB) {
      handleChangeDate(currentDOB, false);
    }
  }, [isEdit]);

  useEffect(() => {
    const currentTitle = watch('title');
    const currentSurname = watch('surName');

    if (currentTitle && currentSurname && !watch('lastName')) {
      const generatedSalutation = `${currentTitle} ${currentSurname}`;
      setValue('lastName', generatedSalutation, programmaticSetValueOptions);
    }
  }, []);

  const checkPostCode = async () => {
    if (!UK_POSTCODE_PATTERN.test(checkPostCodeString.trim())) {
      enqueueSnackbar('Enter a valid UK postcode, e.g. SW1A 1AA', {
        variant: 'warning',
      });
      return;
    }
    setShowFields(true);
    const config = {
      maxBodyLength: Infinity,
      headers: {
        ApiKey: postalCode,
      },
    };
    try {
      const res = await axios.get(
        `${patientGeocodingLocationUrl}?postalCode=${checkPostCodeString}&countryFilter=GB`,
        config,
      );
      const locations = res.data?.locations ?? [];
      setPostalCodeResponse(locations);
      if (!locations.length) {
        enqueueSnackbar('No addresses found for that postcode', {
          variant: 'warning',
        });
      }
    } catch (error) {
      setPostalCodeResponse([]);
      enqueueSnackbar('Postcode lookup failed. Please try again.', {
        variant: 'error',
      });
    }
  };

  /**
   * Rebuilds Full Address from the address parts that are filled in (bug 1:
   * it used to need all five, so a lookup without a house number or county
   * left the required field empty) and revalidates it so a stale
   * "Full Address is required" error disappears.
   */
  const syncFullAddress = (
    parts: {
      houseNumber?: string;
      street?: string;
      town?: string;
      county?: string;
      postCode?: string;
    } = {},
  ) => {
    const joined = joinAddressParts([
      parts.houseNumber ?? houseNumber,
      parts.street ?? street,
      parts.town ?? town,
      parts.county ?? county,
      parts.postCode ?? checkPostCodeString,
    ]);
    if (!joined) return;
    setValue('address', joined, { shouldDirty: true, shouldValidate: true });
  };

  const getAddress = (index: number) => {
    const picked = postalCodeResponse[index]?.address ?? {};
    const next = {
      houseNumber: String(picked.houseNumber ?? ''),
      street: String(picked.street ?? ''),
      town: String(picked.city ?? ''),
      county: String(picked.province ?? ''),
    };
    setShowFields(false);
    setShowAddressFields(true);
    setTown(next.town);
    setHouseNumber(next.houseNumber);
    setCounty(next.county);
    setStreet(next.street);
    syncFullAddress(next);
  };

  const { getPatients } = useAppContext();

  /**
   * Asks the API whether this name + date of birth already exists. Returns true
   * when the save must wait for the user to confirm.
   */
  const needsDuplicateConfirm = async (
    data: FormValuesProps,
    mode: 'create' | 'update',
  ): Promise<boolean> => {
    const query = getDuplicateQuery(data);
    if (!query) return false;
    if (confirmedDuplicateKeyRef.current === duplicateKeyOf(query))
      return false;
    try {
      if (!(await isDuplicateNameDob(query))) return false;
    } catch (error) {
      // A failed check must never block a save.
      return false;
    }
    setDuplicateConfirm({ data, mode });
    return true;
  };

  const onSubmit = async (
    data: FormValuesProps,
    skipDuplicateCheck = false,
  ) => {
    if (!(
      data.consentSMS ||
      data.consentEmail ||
      data.consentPhone ||
      data.consentPost
    )) {
      enqueueSnackbar(
        'Please select at least one contact method: SMS, Email, Call or Postal',
        { variant: 'warning' },
      );
      return;
    }

    if (!skipDuplicateCheck && (await needsDuplicateConfirm(data, 'create'))) {
      return;
    }

    let finalImageUrl: string | null | undefined;
    try {
      finalImageUrl = await resolveFinalImageUrl(data.imageUrl);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Image upload failed', { variant: 'error' });
      return;
    }

    const selectedMethod =
      data.communicationStyle?.method ||
      (data.consentPhone
        ? 'Phone'
        : data.consentSMS
          ? 'SMS'
          : data.consentEmail
            ? 'Email'
            : undefined);
    const dobParts = normalizeDateParts(data.date_of_birth);
    const dobISO = dobParts
      ? `${dobParts.y}-${dobParts.m}-${dobParts.day}`
      : null;
    const payload = {
      title: data.title || undefined,
      firstName: data.firstName,
      surName: data.surName,
      lastName: data.lastName,
      // The Salutation field as the user left it; the backend only falls back
      // to "Title Surname" when nothing is sent.
      salutation: data.lastName,
      date_of_birth: dobISO,

      gp: data.gp,
      gpDetails: {
        name: data.gp,
        address: data.gpAddress,
      },
      phone: data.mobile || data.phone,
      landline: data.landline,
      email: data.email?.trim() || null,
      gender: data.gender ? data.gender : 'Male',
      postCode: data.postCode,
      houseNumber: houseNumber,
      town: town,
      county: county,
      street: street,
      address: data.address,
      notes: data.notes,
      popupNotes: data.popupNotes,
      gdprSmsConsent: data.consentSMS,
      gdprEmailConsent: data.consentEmail,
      gdprPhoneConsent: data.consentPhone,
      gdprPostConsent: data.consentPost,
      nhsEligibility: {
        nhsEligibility: {
          isUnder16: nhsEligibility.under16,
          isOver60: nhsEligibility.over60,
          isStudent: nhsEligibility.student,
          isBenefits: nhsEligibility.benefits,
          isGlaucomaDiabetes: nhsEligibility.glaucomaDiabetes,
          isFamilyHistory: nhsEligibility.familyHistory,
          isOther: nhsEligibility.other
            ? nhsEligibility.otherDetails
            : undefined,
          isEvidenceSeen: nhsEligibility.evidenceSeen,
        },
      },
      communicationStyle: selectedMethod,
      isActive: data.isActive,
      ...(finalImageUrl !== undefined && { imageUrl: finalImageUrl }),
    };

    patientService
      .createPatient(payload)
      .then(function (response) {
        enqueueSnackbar('Patient Created Successfully!', {
          variant: 'success',
        });
        getPatients();
        localStorage.removeItem('draftPrescreening');
        onSubmitForm(response.data);
        closeAfterSave();
      })
      .catch(function (error) {
        enqueueSnackbar(`${error?.message}`, {
          variant: 'error',
        });
      });
  };

  const onUpdate = async (
    data: FormValuesProps,
    skipDuplicateCheck = false,
  ) => {
    if (!(
      data.consentSMS ||
      data.consentEmail ||
      data.consentPhone ||
      data.consentPost
    )) {
      enqueueSnackbar(
        'Please select at least one contact method: SMS, Email, Call or Postal',
        { variant: 'warning' },
      );
      return;
    }

    if (!skipDuplicateCheck && (await needsDuplicateConfirm(data, 'update'))) {
      return;
    }

    let finalImageUrl: string | null | undefined;
    try {
      finalImageUrl = await resolveFinalImageUrl(data.imageUrl);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Image upload failed', { variant: 'error' });
      return;
    }

    const selectedMethod =
      data.communicationStyle?.method ||
      (data.consentPhone
        ? 'Phone'
        : data.consentSMS
          ? 'SMS'
          : data.consentEmail
            ? 'Email'
            : undefined);
    const dobParts = normalizeDateParts(data.date_of_birth);
    const dobISO = dobParts
      ? `${dobParts.y}-${dobParts.m}-${dobParts.day}`
      : null;
    const payload = {
      title: data.title || undefined,
      firstName: data.firstName,
      surName: data.surName,
      lastName: data.lastName,
      salutation: data.lastName,
      date_of_birth: dobISO,
      gp: data.gp,
      gpDetails: {
        name: data.gp,
        address: data.gpAddress,
      },
      phone: data.mobile || data.phone,
      landline: data.landline,
      email: data.email?.trim() || null,
      gender: data.gender ? data.gender : 'Male',
      postCode: data.postCode,
      houseNumber: houseNumber,
      town: town,
      county: county,
      street: street,
      address: data.address,
      notes: data.notes,
      popupNotes: data.popupNotes,
      gdprSmsConsent: data.consentSMS,
      gdprEmailConsent: data.consentEmail,
      gdprPhoneConsent: data.consentPhone,
      gdprPostConsent: data.consentPost,
      nhsEligibility: {
        nhsEligibility: {
          isUnder16: nhsEligibility.under16,
          isOver60: nhsEligibility.over60,
          isStudent: nhsEligibility.student,
          isBenefits: nhsEligibility.benefits,
          isGlaucomaDiabetes: nhsEligibility.glaucomaDiabetes,
          isFamilyHistory: nhsEligibility.familyHistory,
          isOther: nhsEligibility.other
            ? nhsEligibility.otherDetails
            : undefined,
          isEvidenceSeen: nhsEligibility.evidenceSeen,
        },
      },
      communicationStyle: selectedMethod,
      isActive: data.isActive,
      ...(finalImageUrl !== undefined && { imageUrl: finalImageUrl }),
    };

    patientService
      .updatePatient(currentPatient?.id, payload)
      .then(function (response) {
        enqueueSnackbar('Patient Updated Successfully!', {
          variant: 'success',
        });
        onSubmitForm(response?.data);
        closeAfterSave();
      })
      .catch(function (error) {
        enqueueSnackbar(error.message, {
          variant: 'error',
        });
      });
  };

  const [isUnder16, setIsUnder16] = useState(false);

  const handleChangeDate = (dateOfBirth: Date, markDirty = true) => {
    // A stored date_of_birth arrives as an ISO string, the picker gives a Date.
    const dob =
      dateOfBirth instanceof Date ? dateOfBirth : new Date(String(dateOfBirth));
    // A future date is rejected by validation ("Date of birth cannot be in the
    // future"); don't also flag it as under-16, which reads as the wrong reason.
    if (isNaN(dob.getTime()) || dob.getTime() > Date.now()) {
      setIsUnder16(false);
      return;
    }

    setValue('age', getAge(dob), markDirty ? {} : programmaticSetValueOptions);

    const today = new Date();
    const age = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    const dayDiff = today.getDate() - dob.getDate();

    let exactAge = age;
    if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
      exactAge--;
    }

    if (exactAge < 16) {
      setIsUnder16(true);
      enqueueSnackbar('Patient is under 16 — adult supervision required.', {
        variant: 'warning',
        autoHideDuration: 5000,
      });
    } else {
      setIsUnder16(false);
    }
  };

  /** Accepts letters, digits and spaces only; returns false for a rejected key. */
  const handleChange = (value: string) => {
    if (value === '' || /^[A-Za-z\d\s]+$/.test(value)) {
      setCheckPostCodeString(value.toUpperCase());
      return true;
    }
    return false;
  };

  const handleTitleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const selectedTitle = event.target.value;
    // A user-driven edit: the unsaved-changes guard must see the form as dirty.
    setValue('title', selectedTitle, { shouldDirty: true });

    // Auto-set gender for the titles that imply one. Dr/Prof (and any title we
    // don't map) keep the gender already chosen — Gender is required, so
    // clearing it silently blocked saving.
    switch (selectedTitle) {
      case 'Mr':
        setValue('gender', 'Male', { shouldDirty: true });
        break;
      case 'Mrs':
      case 'Miss':
      case 'Ms':
        setValue('gender', 'Female', { shouldDirty: true });
        break;
      case 'Mx':
        setValue('gender', 'Non-Binary', { shouldDirty: true });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const nhs = (currentPatient as any)?.patientNhs?.nhsEligibility;
    if (nhs) {
      setNhsEligibility({
        under16: !!nhs.isUnder16,
        over60: !!nhs.isOver60,
        student: !!nhs.isStudent,
        benefits: !!nhs.isBenefits,
        glaucomaDiabetes: !!nhs.isGlaucomaDiabetes,
        familyHistory: !!nhs.isFamilyHistory,
        other: !!nhs.isOther,
        otherDetails: (nhs.isOther as string) || '',
        evidenceSeen: !!nhs.isEvidenceSeen,
        auditLog: [],
      });
    }
  }, [currentPatient]);

  useEffect(() => {
    const cp: any = currentPatient || {};
    if (cp.postCode || cp.postalCode) {
      setCheckPostCodeString(String(cp.postCode ?? cp.postalCode));
    }
    if (cp.houseNumber || cp.address?.houseNumber) {
      setHouseNumber(String(cp.houseNumber ?? cp.address?.houseNumber ?? ''));
    }
    if (cp.street || cp.address?.street) {
      setStreet(String(cp.street ?? cp.address?.street ?? ''));
    }
    if (cp.town || cp.address?.city) {
      setTown(String(cp.town ?? cp.address?.city ?? ''));
    }
    if (cp.county || cp.address?.province) {
      setCounty(String(cp.county ?? cp.address?.province ?? ''));
    }
  }, [currentPatient]);

  const handleConfirmDuplicate = async () => {
    const pending = duplicateConfirm;
    if (!pending) return;
    const query = getDuplicateQuery(pending.data);
    // Don't ask again for the same patient if the save has to be retried.
    confirmedDuplicateKeyRef.current = query ? duplicateKeyOf(query) : null;
    setIsSavingDuplicate(true);
    try {
      if (pending.mode === 'update') {
        await onUpdate(pending.data, true);
      } else {
        await onSubmit(pending.data, true);
      }
    } finally {
      setIsSavingDuplicate(false);
      setDuplicateConfirm(null);
    }
  };

  return (
    <FormProvider
      methods={methods}
      // Wrapped so react-hook-form's submit event isn't read as a flag.
      onSubmit={handleSubmit((data) =>
        isEdit ? onUpdate(data) : onSubmit(data),
      )}
    >
      <Stack
        spacing={3}
        sx={{ mt: 3, px: 3, pointerEvents: readOnly ? 'none' : 'auto' }}
      >
        <Box
          sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'center',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {readOnly ? (
            <>
              <Avatar
                src={
                  (getValues('imageUrl')
                    ? getR2File(getValues('imageUrl'))
                    : getR2File(currentPatient?.imageUrl)) as string
                }
                alt="Patient Photo"
                sx={{ width: 150, height: 150, mb: 1 }}
                imgProps={{ referrerPolicy: 'no-referrer' }}
              />
            </>
          ) : (
            <>
              <RHFUploadAvatar
                name="imageUrl"
                maxSize={3145728}
                onDrop={handleDrop}
                helperText={
                  <Typography
                    variant="caption"
                    sx={{
                      mt: 2,
                      mx: 'auto',
                      display: 'block',
                      textAlign: 'center',
                      color: 'text.secondary',
                    }}
                  >
                    Allowed *.jpeg, *.jpg, *.png, *.gif
                    <br /> max size of {3145728 / 1024 / 1024}MB
                  </Typography>
                }
              />
              {hasPatientImage && (
                <Button
                  type="button"
                  color="error"
                  size="small"
                  onClick={handleRemoveImage}
                  disabled={isUploadingImage}
                  sx={{ mt: 1 }}
                >
                  Remove Image
                </Button>
              )}
            </>
          )}
        </Box>
        <Box sx={{ display: 'flex' }}>
          <RHFSelect
            sx={{ mr: 1, width: '30%' }}
            native
            name="title"
            label="Title"
            autoFocus
            tabIndex={1}
            onChange={handleTitleChange}
            InputLabelProps={{ shrink: true }}
          >
            {/* A blank option so a patient's title is never wrongly pre-chosen. */}
            <option value="" />
            {titles.map((title) => (
              <option key={title.id} value={title.id}>
                {title.name}
              </option>
            ))}
          </RHFSelect>
          <RHFTextField
            // error={false}
            name="firstName"
            label="First Name*"
            tabIndex={2}
            inputProps={{ maxLength: PATIENT_NAME_MAX }}
            onBlur={() => checkDuplicateByNameDob()}
            onChange={(e) => {
              setValue('firstName', capitalizeWords(e.target.value));
            }}
          />
          <RHFTextField
            sx={{ ml: 1 }}
            // error={false}
            name="surName"
            label="Surname*"
            tabIndex={3}
            inputProps={{ maxLength: PATIENT_NAME_MAX }}
            onBlur={() => checkDuplicateByNameDob()}
            onChange={(e) => {
              setValue('surName', capitalizeWords(e.target.value));
            }}
          />
          <RHFTextField
            sx={{ ml: 1 }}
            error={false}
            name="lastName"
            label="Salutation"
            tabIndex={4}
            inputProps={{ maxLength: PATIENT_SALUTATION_MAX }}
          />
        </Box>
        <Box sx={{ display: 'flex' }}>
          <RHFSelect
            native
            name="gender"
            label="Gender"
            tabIndex={5}
            InputLabelProps={{ shrink: true }}
          >
            <option key="Male" value="Male">
              Male
            </option>
            <option key="Female" value="Female">
              Female
            </option>
            <option key="Non-Binary" value="Non-Binary">
              Non-Binary
            </option>
            <option key="Other" value="Other">
              Other
            </option>
            <option value=""> </option>
          </RHFSelect>
          <Controller
            name="date_of_birth"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DatePicker
                {...field}
                sx={{
                  ml: 2,
                  width: '100%',
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: isUnder16 || error ? '#f44336' : undefined,
                      borderWidth: isUnder16 || error ? '2px' : undefined,
                    },
                  },
                }}
                onChange={(newValue: Date | any) => {
                  field.onChange(newValue);
                  if (newValue) {
                    handleChangeDate(newValue);
                  } else {
                    setIsUnder16(false);
                  }
                }}
                onClose={handleDOBBlur}
                label="Date Of Birth*"
                views={['year', 'month', 'day']}
                format="dd/MM/yyyy"
                maxDate={new Date()}
                slotProps={{
                  textField: {
                    tabIndex: 6,
                    error: !!error || isUnder16,
                    helperText:
                      error?.message ||
                      (isUnder16 ? 'Must be 16 or older' : ''),
                    onBlur: handleDOBBlur,
                  },
                }}
              />
            )}
          />
        </Box>
        <Box sx={{ display: 'flex' }}>
          <RHFTextField
            // error={false}
            name="mobile"
            type="tel"
            label="Mobile"
            tabIndex={7}
            inputProps={{ inputMode: 'tel', maxLength: 32 }}
            onChange={(e) =>
              setValue('mobile', stripNonPhoneCharacters(e.target.value), {
                shouldDirty: true,
              })
            }
          />
          <RHFTextField
            sx={{ ml: 2 }}
            // error={false}
            name="landline"
            type="tel"
            label="Landline"
            tabIndex={8}
            inputProps={{ inputMode: 'tel', maxLength: 32 }}
            onChange={(e) =>
              setValue('landline', stripNonPhoneCharacters(e.target.value), {
                shouldDirty: true,
              })
            }
          />
          <RHFTextField
            sx={{ ml: 2 }}
            // error={false}
            name="email"
            type="email"
            label="Email"
            tabIndex={9}
            inputProps={{ maxLength: 255 }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
          <Controller
            name="postCode"
            control={control}
            defaultValue=""
            render={({ field, fieldState: { error } }) => (
              <RHFTextField
                {...field}
                label="Postcode*"
                error={!!error}
                value={checkPostCodeString}
                helperText={error ? error.message : ''}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  // Only the accepted value reaches the form, so what Yup
                  // validates is what the field shows and the payload sends.
                  if (!handleChange(value)) return;
                  postCodeEditedRef.current = true;
                  field.onChange(value);
                  // Keep a lookup-built address in step with the postcode.
                  if (
                    showAddressFields &&
                    (houseNumber || street || town || county)
                  ) {
                    syncFullAddress({ postCode: value });
                  }
                }}
                sx={{ minWidth: 200 }}
              />
            )}
          />
          <Button
            variant="contained"
            disabled={checkPostCodeString.length > 0 ? false : true}
            onClick={() => checkPostCode()}
          >
            Check Postcode
          </Button>
        </Box>

        {showFields && postalCodeResponse.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select an address:
            </Typography>
            <Box
              sx={{
                maxHeight: 200,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                p: 1,
              }}
            >
              {postalCodeResponse.map((location, index) => (
                <Button
                  key={index}
                  variant="outlined"
                  size="small"
                  onClick={() => getAddress(index)}
                  sx={{
                    display: 'block',
                    textAlign: 'left',
                    mb: 1,
                    textTransform: 'none',
                  }}
                  fullWidth
                >
                  {location.address.houseNumber} {location.address.street},{' '}
                  {location.address.city}, {location.address.province}
                </Button>
              ))}
            </Box>
          </Box>
        )}

        {showAddressFields && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <RHFTextField
                name="houseNumber"
                label="House Number"
                value={houseNumber}
                onChange={(e) => {
                  setHouseNumber(e.target.value);
                  syncFullAddress({ houseNumber: e.target.value });
                }}
              />
              <RHFTextField
                name="street"
                label="Street"
                value={street}
                onChange={(e) => {
                  setStreet(e.target.value);
                  syncFullAddress({ street: e.target.value });
                }}
                sx={{ flexGrow: 1 }}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <RHFTextField
                name="town"
                label="Town/City"
                value={town}
                onChange={(e) => {
                  setTown(e.target.value);
                  syncFullAddress({ town: e.target.value });
                }}
                sx={{ flexGrow: 1 }}
              />
              <RHFTextField
                name="county"
                label="County"
                value={county}
                onChange={(e) => {
                  setCounty(e.target.value);
                  syncFullAddress({ county: e.target.value });
                }}
                sx={{ flexGrow: 1 }}
              />
            </Box>
          </Box>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
          <RHFTextField
            name="address"
            label="Full Address*"
            multiline
            rows={3}
            tabIndex={12}
            placeholder="Enter full address"
            disabled={!perms.canEditPatient && !!isEdit}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', mt: 2 }}>
          <RHFTextField
            name="notes"
            label="Notes"
            multiline
            rows={3}
            tabIndex={13}
            placeholder="Enter patient notes"
            disabled={!perms.canEditPatient && !!isEdit}
          />
          <RHFSwitch
            sx={{ mt: 2 }}
            name="popupNotes"
            label="Popup Notes"
            disabled={!perms.canEditPatient && !!isEdit}
          />
        </Box>

        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Contact Methods*
          </Typography>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              gap: 3,
              flexWrap: 'wrap',
            }}
          >
            <RHFCheckbox
              name="consentSMS"
              label="SMS"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              disabled={!perms.canEditPatient && !!isEdit}
            />
            <RHFCheckbox
              name="consentEmail"
              label="Email"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              disabled={!perms.canEditPatient && !!isEdit}
            />
            <RHFCheckbox
              name="consentPhone"
              label="Call"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              disabled={!perms.canEditPatient && !!isEdit}
            />
            <RHFCheckbox
              name="consentPost"
              label="Postal"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
              disabled={!perms.canEditPatient && !!isEdit}
            />
          </Box>
          {submitAttempted &&
            !(
              (watchedConsentSMS as boolean) ||
              (watchedConsentEmail as boolean) ||
              (watchedConsentPhone as boolean) ||
              (watchedConsentPost as boolean)
            ) && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 1, display: 'block' }}
              >
                Please select at least one contact method (SMS, Email, Call or
                Postal)
              </Typography>
            )}
        </Box>

        {/* NHS Eye Test Eligibility Section */}
        <NHSEyeTestEligibility
          eligibilityData={nhsEligibility}
          onUpdateEligibility={setNhsEligibility}
          disabled={!perms.canEditPatient && !!isEdit}
        />

        {/* GP Information Section */}
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <RHFTextField name="gp" label="GP Name" tabIndex={10} uppercase />
            <RHFTextField
              name="gpAddress"
              label="GP Address"
              tabIndex={11}
              multiline
              minRows={3}
            />
          </Box>
        </Box>
      </Stack>
      <DialogActions>
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="outlined" color="inherit" onClick={onCancel}>
          Cancel
        </Button>
        {!readOnly && (
          <LoadingButton
            type="submit"
            variant="contained"
            loading={isSubmitting}
            disabled={(!!isEdit && !perms.canEditPatient) || isUploadingImage}
            onClick={() => setSubmitAttempted(true)}
          >
            {isEdit ? 'Update' : 'Add'}
          </LoadingButton>
        )}
      </DialogActions>

      <ConfirmDialog
        open={!!duplicateConfirm}
        onClose={() => setDuplicateConfirm(null)}
        title="Possible duplicate patient"
        content={
          duplicateConfirm
            ? `A patient with this name and date of birth already exists (${
                duplicateConfirm.data.firstName
              } ${duplicateConfirm.data.surName}) — ${
                duplicateConfirm.mode === 'update' ? 'save' : 'create'
              } anyway?`
            : ''
        }
        action={
          <LoadingButton
            variant="contained"
            color="warning"
            loading={isSavingDuplicate}
            onClick={handleConfirmDuplicate}
          >
            {isEdit ? 'Save anyway' : 'Create anyway'}
          </LoadingButton>
        }
      />
    </FormProvider>
  );
};

export default AddPatient;
